/**
 * 암호화/복호화 유틸리티 함수
 * 나우앤테스트와의 연동을 위한 AES-256-CBC 암호화 방식
 * 
 * Encryption/Decryption utility functions
 * AES-256-CBC encryption method for integration with Now&Test
 * 
 * 브라우저 환경에서 Web Crypto API를 사용하여 crypto-test의 로직을 구현
 * Implemented crypto-test logic using Web Crypto API for browser environment
 */

// 나우앤테스트에서 제공받은 시크릿 키와 IV
const SECRET_KEY = "elim_kpc_dev_2025092227873926";
const SECRET_IV = "%^&#@$*()+=-_490";

/**
 * SHA256 해시를 hex 문자열로 생성
 * Generate SHA256 hash as hex string
 */
async function sha256Hex(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * hex 문자열을 UTF-8 바이트 배열로 변환
 * Convert hex string to UTF-8 byte array
 * (PHP의 동작을 모방: hex 문자열을 UTF-8 문자열로 해석)
 */
function hexToUtf8Bytes(hex: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(hex);
}


/**
 * 문자열을 암호화합니다 (비동기 버전 - 브라우저 환경용)
 * Encrypt string (async version - for browser environment)
 * 
 * @param str 암호화할 문자열 / String to encrypt
 * @param secret_key 암호화 키 (기본값: SECRET_KEY) / Encryption key (default: SECRET_KEY)
 * @param secret_iv 초기화 벡터용 키 (기본값: SECRET_IV) / Initialization vector key (default: SECRET_IV)
 * @returns 암호화된 문자열 (Promise) / Encrypted string (Promise)
 */
export async function encrypt2Async(
  str: string,
  secret_key: string = SECRET_KEY,
  secret_iv: string = SECRET_IV
): Promise<string> {
  // SHA256 해시로 키 생성
  // PHP의 hash('sha256', ...)는 hex 문자열을 반환하고, openssl_encrypt는 이를 바이너리로 해석
  // hex 문자열을 UTF-8로 해석한 후 처음 32바이트만 사용 (AES-256-CBC는 32바이트 키 필요)
  const keyHash = await sha256Hex(secret_key);
  const keyFull = hexToUtf8Bytes(keyHash); // hex 문자열을 UTF-8로 해석 (64바이트)
  const key = keyFull.slice(0, 32); // 처음 32바이트만 사용

  // SHA256 해시로 IV 생성
  // PHP의 substr(hash('sha256', $secret_iv), 0, 16)는 hex 문자열의 처음 16자를 의미
  // PHP의 openssl_encrypt는 hex 문자열을 바이너리로 해석하므로 UTF-8로 변환 (16바이트)
  const ivHash = await sha256Hex(secret_iv);
  const ivHex = ivHash.substring(0, 16); // hex 문자열의 처음 16자
  const iv = hexToUtf8Bytes(ivHex); // hex 문자열을 UTF-8로 해석 (16바이트)

  // AES-256-CBC 암호화를 위한 키 임포트
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key.buffer as ArrayBuffer,
    { name: 'AES-CBC', length: 256 },
    false,
    ['encrypt']
  );

  // 문자열을 UTF-8 바이트로 변환
  const encoder = new TextEncoder();
  const data = encoder.encode(str);

  // AES-256-CBC 암호화
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv: iv as unknown as ArrayBuffer },
    cryptoKey,
    data
  );

  // Base64 인코딩
  const encryptedArray = new Uint8Array(encrypted);
  let enc_str = '';
  for (let i = 0; i < encryptedArray.length; i++) {
    enc_str += String.fromCharCode(encryptedArray[i]);
  }
  enc_str = btoa(enc_str);

  // padding "=" 제거
  enc_str = enc_str.replace(/=+$/, '');

  // "/"를 "$"로 교체
  enc_str = enc_str.replace(/\//g, '$');

  return enc_str;
}


/**
 * 암호화된 문자열을 복호화합니다 (비동기 버전 - 브라우저 환경용)
 * Decrypt encrypted string (async version - for browser environment)
 * 
 * @param str 복호화할 암호화 문자열 / Encrypted string to decrypt
 * @param secret_key 암호화 키 (기본값: SECRET_KEY) / Encryption key (default: SECRET_KEY)
 * @param secret_iv 초기화 벡터용 키 (기본값: SECRET_IV) / Initialization vector key (default: SECRET_IV)
 * @returns 복호화된 문자열 (Promise) / Decrypted string (Promise)
 * @throws {Error} 복호화 실패 시 에러 발생 / Throws error on decryption failure
 */
export async function decrypt2Async(
  str: string,
  secret_key: string = SECRET_KEY,
  secret_iv: string = SECRET_IV
): Promise<string> {
  // "$"를 "/"로 복원
  let dec_str = str.replace(/\$/g, '/');

  // SHA256 해시로 키 생성
  // PHP의 hash('sha256', ...)는 hex 문자열을 반환하고, openssl_encrypt는 이를 바이너리로 해석
  // hex 문자열을 UTF-8로 해석한 후 처음 32바이트만 사용 (AES-256-CBC는 32바이트 키 필요)
  const keyHash = await sha256Hex(secret_key);
  const keyFull = hexToUtf8Bytes(keyHash); // hex 문자열을 UTF-8로 해석 (64바이트)
  const key = keyFull.slice(0, 32); // 처음 32바이트만 사용

  // SHA256 해시로 IV 생성
  // PHP의 substr(hash('sha256', $secret_iv), 0, 16)는 hex 문자열의 처음 16자를 의미
  // PHP의 openssl_encrypt는 hex 문자열을 바이너리로 해석하므로 UTF-8로 변환 (16바이트)
  const ivHash = await sha256Hex(secret_iv);
  const ivHex = ivHash.substring(0, 16); // hex 문자열의 처음 16자
  const iv = hexToUtf8Bytes(ivHex); // hex 문자열을 UTF-8로 해석 (16바이트)

  try {
    // Base64 디코딩 (padding 자동 복원)
    while (dec_str.length % 4 !== 0) {
      dec_str += '=';
    }
    const encrypted = Uint8Array.from(atob(dec_str), c => c.charCodeAt(0));

    // AES-256-CBC 복호화를 위한 키 임포트
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      key.buffer as ArrayBuffer,
      { name: 'AES-CBC', length: 256 },
      false,
      ['decrypt']
    );

    // AES-256-CBC 복호화
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv: iv as unknown as ArrayBuffer },
      cryptoKey,
      encrypted.buffer as ArrayBuffer
    );

    // UTF-8로 디코딩
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(decrypted);
  } catch (error) {
    throw new Error(`복호화 실패: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * URL 파라미터에서 사용자 정보 추출 (비동기)
 * Extract user information from URL parameters (async)
 * 
 * @param paramName 파라미터 이름 (기본값: 'user') / Parameter name (default: 'user')
 * @returns 복호화된 사용자 정보 (Promise) / Decrypted user information (Promise)
 */
export async function getUserInfoFromUrl(paramName: string = 'user'): Promise<string | null> {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const encryptedUser = urlParams.get(paramName);
    
    if (!encryptedUser) {
      return null;
    }
    
    return await decrypt2Async(encryptedUser);
  } catch (error) {
    console.error('URL에서 사용자 정보 추출 오류 / Error extracting user info from URL:', error);
    return null;
  }
}

/**
 * 암호화/복호화 테스트 함수 (비동기)
 * Test function for encryption/decryption (async)
 */
export async function testEncryption(): Promise<void> {
  const testString = "한동주_123456";
  const expectedEncrypted = "xJjoVoEY8CrXcjqcXdwe3eTXiZzTWSyJvnAviTBuL08";
  
  console.log("=== 암호화/복호화 테스트 / Encryption/Decryption Test ===");
  console.log("원본 문자열 / Original string:", testString);
  console.log("예상 암호화 결과 / Expected encrypted result:", expectedEncrypted);
  
  try {
    // 암호화 테스트
    // Encryption test
    const encrypted = await encrypt2Async(testString);
    console.log("실제 암호화 결과 / Actual encrypted result:", encrypted);
    console.log("암호화 일치 여부 / Encryption match:", encrypted === expectedEncrypted);
    
    // 복호화 테스트
    // Decryption test
    const decrypted = await decrypt2Async(expectedEncrypted);
    console.log("복호화 결과 / Decrypted result:", decrypted);
    console.log("복호화 일치 여부 / Decryption match:", decrypted === testString);
    
    // 라운드트립 테스트 (암호화 후 복호화)
    // Round-trip test (encrypt then decrypt)
    const roundTripDecrypted = await decrypt2Async(encrypted);
    console.log("라운드트립 테스트 결과 / Round-trip test result:", roundTripDecrypted);
    console.log("라운드트립 일치 여부 / Round-trip match:", roundTripDecrypted === testString);
  } catch (error) {
    console.error("테스트 실패 / Test failed:", error);
  }
}
